import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BasicInput, EditTemplateIcon, Typography, ViewTemplateIcon } from '@dev/ee-components';
import { getPaidNewsletterCreatorStore, SetPaidNewsletterErrors, SetEmailTemplateOpenType, SetTemplatePreview, ModifySubscriptionCancelationTemplate } from '@/store/paidNewsletterCreator/actions';
import { TemplatePreviewButton } from '@/assets/TemplatePreviewButton';
import { EmailTemplateType, PaidNewsletterErrorNames } from '@/store/paidNewsletterCreator/types';
import { EmailDropdown } from '@/components/PaidNewsletterCreator/components/EmailDropdown';
import { getApiPath } from '@/services/Secuirty';
import { EmailDropdownWrapper, FieldGroupWrapper, ScreenshotHover, ScreenshotWrapper, Step6EditContentColumns, Step6EditContentWrapper } from './styles';
import { EmailSubject } from './components/EmailSubject';
import { useGetDefaultSender } from '../../../../services/hooks/useGetDefaultSender';

export const Step6EditContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { email: defaultEmail, name: defaultName } = useGetDefaultSender() ?? {};

    const { paidNewsletterObject, subscriptionCancelationTemplatePayload, errors } = useSelector(getPaidNewsletterCreatorStore);
 
    const templateScreenshot = paidNewsletterObject?.CancellationTemplate?.Screenshot;

    const { FromName, FromEmail } = paidNewsletterObject?.CancellationTemplate || {};

    const { fromEmail: fromEmailPayload, fromName: fromNamePayload } = subscriptionCancelationTemplatePayload || {};
    
    const viewTemplate = (): void => {
        const previewHtmlContent = paidNewsletterObject?.CancellationTemplate?.Body?.Parts?.find((part) => part.ContentType === 'text/html')?.Content;
        dispatch(SetTemplatePreview(previewHtmlContent));
    };

    const editTemplate = (): void => {
        dispatch(SetEmailTemplateOpenType(EmailTemplateType.SUBSCRIPTION_CANCELATION));
    };

    const changeFromNameHandle = (ev) => {
        setFromName(ev.currentTarget.value);
    };

    const setFromName = (value: string): void => {
        dispatch(ModifySubscriptionCancelationTemplate({ fromName: value }));
    };
    
    const setFromEmail = (value: string): void => {
        dispatch(ModifySubscriptionCancelationTemplate({ fromEmail: value }));

        if (!errors) {
            return;
        }
        dispatch(SetPaidNewsletterErrors());
    };

    const isInvalidEmail = errors?.[PaidNewsletterErrorNames.INVALID_FROM_EMAIL];
    const errorMessage = isInvalidEmail ? t('emailnotassociatederror') : t('thisfieldisrequired');

    return (
        <Step6EditContentWrapper>
            <Typography variant="body1">{t('subscriptioncancelation_description')}</Typography>
            <Step6EditContentColumns>
                <div>
                <ScreenshotWrapper>
                    <ScreenshotHover>
                        <TemplatePreviewButton
                            id="paid-newsletter-edit-subscription-cancelation-template"
                            buttonIcon={<EditTemplateIcon width={31.5} height={31.5} />}
                            buttonLabel={t('edit')}
                            handleOnClick={editTemplate}
                        />
                        <TemplatePreviewButton
                            id="paid-newsletter-view-subscription-cancelation-template"
                            buttonIcon={<ViewTemplateIcon width={28.8} height={28.8} />}
                            buttonLabel={t('view')}
                            handleOnClick={viewTemplate}
                        />
                    </ScreenshotHover>
                    {templateScreenshot && <img src={`${getApiPath()}/userfile${encodeURI(templateScreenshot)}`} />}
                </ScreenshotWrapper>
                </div>
                <div>
                    <FieldGroupWrapper>
                        <div>
                            <BasicInput
                                id="paid-newsletter-subscription-cancelation-from-name"
                                label={t('fromname')}
                                value={fromNamePayload ?? FromName ?? defaultName ?? ''}
                                onChange={changeFromNameHandle}
                            />
                        </div>
                        <EmailDropdownWrapper>
                            <EmailDropdown
                                setEmail={setFromEmail}
                                email={fromEmailPayload ?? (FromEmail || defaultEmail || '')} //it's on purpose, the backend changes null to an empty string
                                hasError={fromEmailPayload === '' || isInvalidEmail}
                                errorMessage={errorMessage}
                                id="paid-newsletter-subscription-cancelation-from-email"
                                label={t('frommail')}
                                placeholder={t('enteravalidemailadress')}
                            />
                        </EmailDropdownWrapper>
                    </FieldGroupWrapper>
                    <FieldGroupWrapper>
                        <EmailSubject />
                    </FieldGroupWrapper>
                </div>
            </Step6EditContentColumns>
        </Step6EditContentWrapper>
    );
};
